import {
  Loader2,
  Pause,
  Play,
  Plus,
  Scissors,
  SkipBack,
  Undo2,
  ZoomIn,
  ZoomOut,
} from "lucide-react";

export type EditorHeaderProps = {
  isPlaying: boolean;
  audioSrc: string | null;
  currentTime: number;
  storyDuration: number;
  isUpdatingAudio: boolean;
  canUndo: boolean;
  canRedo: boolean;
  toolMode: "select" | "cut";
  fmtTime: (s: number) => string;
  onPlayPause: () => void;
  onSkipBack: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onToolModeToggle: () => void;
  onOpenGallery: () => void;
};

export function EditorHeader({
  isPlaying,
  audioSrc,
  currentTime,
  storyDuration,
  isUpdatingAudio,
  canUndo,
  canRedo,
  toolMode,
  fmtTime,
  onPlayPause,
  onSkipBack,
  onUndo,
  onRedo,
  onZoomIn,
  onZoomOut,
  onToolModeToggle,
  onOpenGallery,
}: EditorHeaderProps) {
  return (
    <div className="flex flex-wrap gap-4 items-center justify-between px-5 py-3 border-b border-[#e2e8f0]">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onSkipBack}
            className="flex h-8 w-8 items-center justify-center rounded-md text-[#45556c] hover:bg-[#f1f5f9]"
            aria-label="Retour au début"
          >
            <SkipBack className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onPlayPause}
            disabled={!audioSrc}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-[#007aff] text-white hover:bg-[#006ae0] disabled:opacity-40"
            aria-label={isPlaying ? "Pause" : "Lecture"}
          >
            {isPlaying
              ? <Pause className="h-3.5 w-3.5 fill-current" />
              : <Play className="h-3.5 w-3.5 fill-current" />}
          </button>
        </div>

        <span className="text-[14px] font-semibold text-[#0f172b] tabular-nums">
          {fmtTime(currentTime)}
          <span className="mx-1.5 text-[#e2e8f0]">|</span>
          <span className="font-normal text-[#94a3b8]">{fmtTime(storyDuration)}</span>
        </span>
      </div>

      <div className="flex items-center gap-2">
        {isUpdatingAudio && (
          <div className="flex items-center gap-2 animate-in fade-in duration-200">
            <span className="text-[13px] text-[#475569] leading-none">
              Mise à jour de l'audio...
            </span>
            <div className="h-4 w-4 rounded-full border-2 border-[#007aff] border-t-transparent animate-spin shrink-0" />
          </div>
        )}

        <div className="inline-flex items-stretch rounded-lg border border-[#cbd5e1] bg-white shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={onUndo}
            disabled={!canUndo}
            className="inline-flex h-9 items-center gap-1.5 px-3 text-[13px] font-medium text-[#0f172b] hover:bg-slate-50 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Annuler la dernière modification"
            title="Annuler (Ctrl+Z)"
          >
            <Undo2 className="h-4 w-4" />
            Annuler
          </button>
          <div className="w-px bg-[#e2e8f0]" />
          <button
            type="button"
            onClick={onRedo}
            disabled={!canRedo}
            className="inline-flex h-9 w-9 items-center justify-center text-[#0f172b] hover:bg-slate-50 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Rétablir la dernière modification annulée"
            title="Rétablir (Ctrl+Shift+Z)"
          >
            <Undo2 className="h-4 w-4 scale-x-[-1]" />
          </button>
        </div>

        <div className="flex items-center gap-0.5 rounded-lg border border-[#cbd5e1] bg-white shadow-sm">
          <button
            type="button"
            onClick={onZoomOut}
            className="flex h-9 w-9 items-center justify-center rounded-l-lg text-[#0f172b] hover:bg-slate-50"
            aria-label="Dézoomer"
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onZoomIn}
            className="flex h-9 w-9 items-center justify-center rounded-r-lg text-[#0f172b] hover:bg-slate-50"
            aria-label="Zoomer"
          >
            <ZoomIn className="h-4 w-4" />
          </button>
        </div>

        <button
          type="button"
          onClick={onToolModeToggle}
          className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border transition-all shadow-sm ${
            toolMode === "cut"
              ? "border-[#c8009c] bg-[#fdebf9] text-[#c8009c]"
              : "border-[#cbd5e1] bg-white text-[#0f172b] hover:bg-slate-50"
          }`}
          aria-label={toolMode === "cut" ? "Désactiver le mode ciseaux" : "Activer le mode ciseaux"}
          aria-pressed={toolMode === "cut"}
          title={
            toolMode === "cut"
              ? "Cliquez sur un son pour le couper. Cliquez à nouveau ici pour quitter le mode."
              : "Mode ciseaux : cliquez sur un son pour le découper à la position du clic."
          }
        >
          <Scissors className="h-4 w-4" />
        </button>

        <button
          type="button"
          onClick={onOpenGallery}
          className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-[#cbd5e1] bg-white px-4 text-[13px] font-medium text-[#475569] hover:bg-slate-50 transition-all shadow-sm"
        >
          <Plus className="h-3.5 w-3.5 text-[#0f172b] stroke-[2.5]" />
          Galerie audio
        </button>
      </div>
    </div>
  );
}
